/**
 * Shell-mounted pull-to-refresh host + visual affordance (MOB-02).
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useOverlayBackDismiss } from '../../shared/context/OverlayBackDismissContext.js';
import {
  usePullToRefresh,
  type PullToRefreshPhase,
} from '../../shared/hooks/usePullToRefresh.js';
import { showToast } from '../../shared/ui/toastStore.js';
import { CUSTOMER_SHELL_WIDTH_CLASS, CUSTOMER_TOP_BAR_CLEARANCE_TOP_CLASS } from '../customerTopBarStyles.js';
import { isCustomerFormDirty } from './customerFormDirtyGate.js';
import { isPaymentPending } from './paymentPendingGate.js';
import { runCustomerPullRefresh } from './runCustomerPullRefresh.js';

export interface PullToRefreshIndicatorProps {
  readonly phase: PullToRefreshPhase;
  readonly pullDistance: number;
  readonly pastThreshold: boolean;
}

function resolveIndicatorMessage(
  phase: PullToRefreshPhase,
  pastThreshold: boolean,
  t: (key: string) => string,
): string | null {
  if (phase === 'refreshing') {
    return t('pwa.pullToRefresh.refreshing');
  }
  if (phase === 'release' || pastThreshold) {
    return t('pwa.pullToRefresh.release');
  }
  if (phase === 'pulling') {
    return t('pwa.pullToRefresh.hint');
  }
  return null;
}

/** Visual PTR chrome — hint / release / refreshing. */
export function PullToRefreshIndicator({
  phase,
  pullDistance,
  pastThreshold,
}: PullToRefreshIndicatorProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const message = resolveIndicatorMessage(phase, pastThreshold, t);

  if (message === null && pullDistance <= 0) {
    return null;
  }

  const visible = message !== null;
  const translateY = Math.min(pullDistance, 96);

  return (
    <div
      className={`customer-safe-area-x pointer-events-none sticky ${CUSTOMER_TOP_BAR_CLEARANCE_TOP_CLASS} z-30 px-3 ${CUSTOMER_SHELL_WIDTH_CLASS} lg:[html[data-sidebar-visible]_&]:pl-56`}
      data-testid="customer-pull-to-refresh-indicator"
      aria-hidden={!visible}
    >
      <div
        className={[
          'flex items-center justify-center gap-2 rounded-md border border-[var(--color-border)]',
          'bg-[var(--color-surface-elevated,var(--color-surface))] px-3 py-2 text-sm',
          'text-[var(--color-on-surface)] shadow-[var(--shadow-popover,0_4px_12px_rgba(0,0,0,0.08))]',
          'transition-opacity duration-150',
          visible ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
        style={{
          transform: `translateY(${Math.max(0, translateY - 48)}px)`,
        }}
        role="status"
        aria-live="polite"
        data-ptr-phase={phase}
      >
        {phase === 'refreshing' ? (
          <span
            className="inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)]"
            aria-hidden="true"
            data-testid="customer-pull-to-refresh-spinner"
          />
        ) : null}
        <p className="m-0">{message ?? t('pwa.pullToRefresh.hint')}</p>
      </div>
    </div>
  );
}

/**
 * Document PTR host — mount once inside `OverlayBackDismissProvider`.
 */
export function CustomerPullToRefresh(): JSX.Element {
  const { t } = useTranslation('customer');
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { overlayDismiss } = useOverlayBackDismiss();

  const onRefresh = useCallback(async (): Promise<void> => {
    try {
      await runCustomerPullRefresh({ queryClient, pathname });
    } catch (err) {
      showToast({
        message: t('pwa.pullToRefresh.failed'),
        variant: 'danger',
      });
      // Surface via toast; keep rejection out of the gesture FSM.
      if (!(err instanceof Error)) {
        console.error('Customer pull-to-refresh failed', err);
        return;
      }
      console.error(err);
    }
  }, [pathname, queryClient, t]);

  /**
   * G8: disable gesture while payment pending or dirty draft surfaces.
   * Checkout/payment routes stay gesture-enabled with invalidate-only reload forbid
   * in `runCustomerPullRefresh` — payment-pending additionally blocks the gesture.
   */
  const isDisabled = useCallback((): boolean => {
    if (isPaymentPending()) {
      return true;
    }
    if (isCustomerFormDirty()) {
      return true;
    }
    return false;
  }, []);

  const { phase, pullDistance, pastThreshold } = usePullToRefresh({
    onRefresh,
    overlayDismiss,
    isDisabled,
  });

  return (
    <div data-testid="customer-pull-to-refresh-host">
      <PullToRefreshIndicator
        phase={phase}
        pullDistance={pullDistance}
        pastThreshold={pastThreshold}
      />
    </div>
  );
}
